import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { format } from 'date-fns';
import {
    Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Tabs, Tab, Box, Card, CardContent,
    Typography, Badge, Tooltip, IconButton, Alert, Divider,
    ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem,
    Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TuneIcon from '@mui/icons-material/Tune';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataTable from '../../Generic/DataTable';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const List = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const localizedTextsMap = useLocalizedTextsMap();
    const { enqueueSnackbar } = useSnackbar();

    useSetMenu(mainStore.ContentApprovalMenuType, mainStore.ContentApprovalMenuNo);

    // Main category: 'submissions' or 'requests'
    const [mainCategory, setMainCategory] = useState('submissions');
    // Sub-type within category: 'all', 'toolPassport', 'criteria', 'toolFeature'
    const [subType, setSubType] = useState('all');

    // Map to internal tab indices for backwards compatibility
    // Tab indices: 0: All, 1: ToolPassports, 2: Criteria & Values (combined), 3: ToolFeatures, 4: ToolPassportRequests, 5: CriteriaRequests, 6: CriteriaValueRequests, 7: ToolFeatureRequests
    const getActiveTab = () => {
        if (mainCategory === 'submissions') {
            switch (subType) {
                case 'all': return 0;
                case 'toolPassport': return 1;
                case 'criteria': return 2;
                case 'toolFeature': return 3;
                default: return 0;
            }
        } else {
            // requests
            switch (subType) {
                case 'all': return 0; // Will show all pending including requests
                case 'toolPassport': return 4;
                case 'criteria': return 5;
                case 'criteriaValue': return 6;
                case 'toolFeature': return 7;
                default: return 4;
            }
        }
    };
    const activeTab = getActiveTab();
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [processingAction, setProcessingAction] = useState(false);

    // Data states
    const [pendingCounts, setPendingCounts] = useState({
        pendingToolPassports: 0,
        pendingCriteria: 0,
        pendingCriteriaValues: 0,
        pendingCriteriaAndValues: 0, // Combined count
        pendingToolFeatures: 0,
        pendingToolPassportRequests: 0,
        pendingCriteriaRequests: 0,
        pendingCriteriaValueRequests: 0,
        pendingToolFeatureRequests: 0,
        totalPending: 0
    });
    const [dataVersion, setDataVersion] = useState(0); // Used to force DataTable re-render
    const [allPending, setAllPending] = useState([]);
    const [toolPassports, setToolPassports] = useState([]);
    const [criteriaAndValues, setCriteriaAndValues] = useState([]); // Combined criteria and values
    const [toolFeatures, setToolFeatures] = useState([]);
    const [toolFeaturesGrouped, setToolFeaturesGrouped] = useState([]); // Grouped by tool
    const [toolFeaturesViewMode, setToolFeaturesViewMode] = useState('grouped'); // 'grouped' or 'individual'
    const [expandedToolAccordions, setExpandedToolAccordions] = useState({}); // Track expanded accordions
    const [toolPassportRequests, setToolPassportRequests] = useState([]);
    const [criteriaRequests, setCriteriaRequests] = useState([]);
    const [criteriaValueRequests, setCriteriaValueRequests] = useState([]);
    const [toolFeatureRequests, setToolFeatureRequests] = useState([]);

    // Review dialog state (for both approve and reject)
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [reviewAction, setReviewAction] = useState(null); // 'approve' or 'reject'
    const [selectedItem, setSelectedItem] = useState(null);
    const [reviewComments, setReviewComments] = useState("");

    // Legacy rejection dialog state (keeping for compatibility)
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

    // View Changes dialog state for ToolPassportRequest
    const [viewChangesDialogOpen, setViewChangesDialogOpen] = useState(false);
    const [viewChangesData, setViewChangesData] = useState(null);
    const [loadingChanges, setLoadingChanges] = useState(false);

    // Fetch pending counts
    const fetchPendingCounts = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ContentApproval/PendingCounts');
            // Calculate combined criteria and values count
            const counts = {
                ...response.data,
                pendingCriteriaAndValues: (response.data.pendingCriteria || 0) + (response.data.pendingCriteriaValues || 0)
            };
            setPendingCounts(counts);
        } catch (error) {
            console.error('Error fetching pending counts:', error);
        }
    }, [axiosPrivate]);

    // Fetch data for a specific tab (or current activeTab if not specified)
    const fetchDataForTab = useCallback(async (tabIndex) => {
        const tab = tabIndex !== undefined ? tabIndex : activeTab;
        try {
            switch (tab) {
                case 0: // All pending
                    const allResponse = await axiosPrivate.get('/ContentApproval/AllPending');
                    setAllPending(allResponse.data);
                    break;
                case 1: // Tool Passports
                    const tpResponse = await axiosPrivate.get('/ContentApproval/PendingToolPassports');
                    setToolPassports(tpResponse.data);
                    break;
                case 2: // Criteria & Values (combined)
                    const cavResponse = await axiosPrivate.get('/ContentApproval/PendingCriteriaAndValues');
                    // Add unique IDs for DataTable
                    const dataWithUniqueIds = cavResponse.data.map(item => ({
                        ...item,
                        uniqueId: `${item.itemType}-${item.id}`
                    }));
                    setCriteriaAndValues(dataWithUniqueIds);
                    break;
                case 3: // Tool Features
                    const tfResponse = await axiosPrivate.get('/ContentApproval/PendingToolFeatures');
                    setToolFeatures(tfResponse.data);
                    // Also fetch grouped tool features
                    const tfGroupedResponse = await axiosPrivate.get('/ContentApproval/PendingToolFeaturesGrouped');
                    setToolFeaturesGrouped(tfGroupedResponse.data);
                    break;
                case 4: // Tool Passport Edit/Delete Requests
                    const tprResponse = await axiosPrivate.get('/ContentApproval/PendingToolPassportRequests');
                    setToolPassportRequests(tprResponse.data);
                    break;
                case 5: // Criteria Edit/Delete Requests
                    const crResponse = await axiosPrivate.get('/ContentApproval/PendingCriteriaRequests');
                    setCriteriaRequests(crResponse.data);
                    break;
                case 6: // CriteriaValue Edit/Delete Requests
                    const cvrResponse = await axiosPrivate.get('/ContentApproval/PendingCriteriaValueRequests');
                    setCriteriaValueRequests(cvrResponse.data);
                    break;
                case 7: // ToolFeature Edit/Delete Requests
                    const tfrResponse = await axiosPrivate.get('/ContentApproval/PendingToolFeatureRequests');
                    setToolFeatureRequests(tfrResponse.data);
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error('Error fetching data for tab:', error);
            throw error;
        }
    }, [axiosPrivate, activeTab]);

    // Fetch all request types (for "All Requests" view)
    const fetchAllRequests = useCallback(async () => {
        try {
            const [tprResponse, crResponse, cvrResponse, tfrResponse] = await Promise.all([
                axiosPrivate.get('/ContentApproval/PendingToolPassportRequests'),
                axiosPrivate.get('/ContentApproval/PendingCriteriaRequests'),
                axiosPrivate.get('/ContentApproval/PendingCriteriaValueRequests'),
                axiosPrivate.get('/ContentApproval/PendingToolFeatureRequests')
            ]);
            setToolPassportRequests(tprResponse.data);
            setCriteriaRequests(crResponse.data);
            setCriteriaValueRequests(cvrResponse.data);
            setToolFeatureRequests(tfrResponse.data);
        } catch (error) {
            console.error('Error fetching all requests:', error);
            throw error;
        }
    }, [axiosPrivate]);

    // Fetch data based on active tab (wrapper that handles loading state)
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // When in 'requests' category with 'all' subType, fetch all request types
            if (mainCategory === 'requests' && subType === 'all') {
                await fetchAllRequests();
            } else {
                await fetchDataForTab(activeTab);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setLoading(false);
            setDataVersion(v => v + 1); // Increment to force DataTable re-render
        }
    }, [fetchDataForTab, fetchAllRequests, activeTab, mainCategory, subType, enqueueSnackbar, t]);

    useEffect(() => {
        fetchPendingCounts();
        fetchData();
    }, [fetchPendingCounts, fetchData]);

    const handleMainCategoryChange = (event, newCategory) => {
        if (newCategory !== null) {
            setMainCategory(newCategory);
            setSubType('all');
            setSearchQuery("");
        }
    };

    const handleSubTypeChange = (event) => {
        setSubType(event.target.value);
        setSearchQuery("");
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Combined all requests for the "All Requests" view
    const allRequests = useMemo(() => {
        const tpReqs = toolPassportRequests.map(r => ({ ...r, contentType: 'ToolPassportRequest', uniqueId: `tpr-${r.id}` }));
        const cReqs = criteriaRequests.map(r => ({ ...r, contentType: 'CriteriaRequest', uniqueId: `cr-${r.id}` }));
        const cvReqs = criteriaValueRequests.map(r => ({ ...r, contentType: 'CriteriaValueRequest', uniqueId: `cvr-${r.id}` }));
        const tfReqs = toolFeatureRequests.map(r => ({ ...r, contentType: 'ToolFeatureRequest', uniqueId: `tfr-${r.id}` }));
        return [...tpReqs, ...cReqs, ...cvReqs, ...tfReqs].sort((a, b) =>
            new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
        );
    }, [toolPassportRequests, criteriaRequests, criteriaValueRequests, toolFeatureRequests]);

    // Get current data based on main category and sub type
    const getCurrentData = () => {
        if (mainCategory === 'submissions') {
            switch (subType) {
                case 'all': return allPending;
                case 'toolPassport': return toolPassports;
                case 'criteria': return criteriaAndValues;
                case 'toolFeature': return toolFeatures;
                default: return allPending;
            }
        } else {
            // requests
            switch (subType) {
                case 'all': return allRequests;
                case 'toolPassport': return toolPassportRequests;
                case 'criteria': return criteriaRequests;
                case 'criteriaValue': return criteriaValueRequests;
                case 'toolFeature': return toolFeatureRequests;
                default: return allRequests;
            }
        }
    };

    // Filter data based on search query
    const filteredData = useMemo(() => {
        const data = getCurrentData();
        if (!searchQuery) return data;
        const query = searchQuery.toLowerCase();
        return data.filter(item =>
            item.name?.toLowerCase().includes(query) ||
            item.value?.toLowerCase().includes(query) ||
            item.displayName?.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query) ||
            item.subcategoryName?.toLowerCase().includes(query) ||
            item.categoryName?.toLowerCase().includes(query) ||
            item.contentType?.toLowerCase().includes(query) ||
            item.itemType?.toLowerCase().includes(query) ||
            item.criteriaName?.toLowerCase().includes(query) ||
            item.originalToolName?.toLowerCase().includes(query) ||
            item.originalName?.toLowerCase().includes(query) ||
            item.originalValue?.toLowerCase().includes(query) ||
            item.requestType?.toLowerCase().includes(query)
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainCategory, subType, allPending, toolPassports, criteriaAndValues, toolFeatures, toolPassportRequests, criteriaRequests, criteriaValueRequests, toolFeatureRequests, allRequests, searchQuery]);

    // Open review dialog (for both approve and reject)
    const openReviewDialog = (item, contentType, action) => {
        setSelectedItem({ ...item, contentType: contentType || item.contentType });
        setReviewAction(action);
        setReviewComments("");
        setReviewDialogOpen(true);
    };

    // Close review dialog
    const closeReviewDialog = () => {
        setReviewDialogOpen(false);
        setSelectedItem(null);
        setReviewAction(null);
        setReviewComments("");
    };

    // Helper function to remove item from appropriate state after approval/rejection
    const removeItemFromState = useCallback((item, contentType) => {
        const type = contentType || item.contentType;
        const itemId = item.id;

        switch (type) {
            case 'ToolPassport':
                setToolPassports(prev => prev.filter(i => i.id !== itemId));
                setAllPending(prev => prev.filter(i => !(i.id === itemId && i.contentType === 'ToolPassport')));
                break;
            case 'Criteria':
                setCriteriaAndValues(prev => prev.filter(i => !(i.id === itemId && i.itemType === 'Criteria')));
                setAllPending(prev => prev.filter(i => !(i.id === itemId && i.contentType === 'Criteria')));
                break;
            case 'CriteriaValue':
                setCriteriaAndValues(prev => prev.filter(i => !(i.id === itemId && i.itemType === 'CriteriaValue')));
                setAllPending(prev => prev.filter(i => !(i.id === itemId && i.contentType === 'CriteriaValue')));
                break;
            case 'ToolFeature':
                setToolFeatures(prev => prev.filter(i => i.id !== itemId));
                setAllPending(prev => prev.filter(i => !(i.id === itemId && i.contentType === 'ToolFeature')));
                // Also update grouped tool features
                setToolFeaturesGrouped(prev => {
                    return prev.map(group => ({
                        ...group,
                        features: group.features.filter(f => f.id !== itemId),
                        totalFeatures: group.features.filter(f => f.id !== itemId).length
                    })).filter(group => group.features.length > 0);
                });
                break;
            case 'ToolPassportRequest':
                setToolPassportRequests(prev => prev.filter(i => i.id !== itemId));
                break;
            case 'CriteriaRequest':
                setCriteriaRequests(prev => prev.filter(i => i.id !== itemId));
                break;
            case 'CriteriaValueRequest':
                setCriteriaValueRequests(prev => prev.filter(i => i.id !== itemId));
                break;
            case 'ToolFeatureRequest':
                setToolFeatureRequests(prev => prev.filter(i => i.id !== itemId));
                break;
            default:
                break;
        }
    }, []);

    // Handle review action (approve or reject)
    const handleReviewAction = async () => {
        if (!selectedItem || !reviewAction) return;

        setProcessingAction(true);
        try {
            const type = selectedItem.contentType;
            let endpoint = '';
            switch (type) {
                case 'ToolPassport':
                    endpoint = '/ContentApproval/ApproveToolPassport';
                    break;
                case 'Criteria':
                    endpoint = '/ContentApproval/ApproveCriteria';
                    break;
                case 'CriteriaValue':
                    endpoint = '/ContentApproval/ApproveCriteriaValue';
                    break;
                case 'ToolFeature':
                    endpoint = '/ContentApproval/ApproveToolFeature';
                    break;
                case 'ToolPassportRequest':
                    endpoint = '/ContentApproval/ApproveToolPassportRequest';
                    break;
                case 'CriteriaRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaRequest';
                    break;
                case 'CriteriaValueRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaValueRequest';
                    break;
                case 'ToolFeatureRequest':
                    endpoint = '/ContentApproval/ApproveToolFeatureRequest';
                    break;
                default:
                    throw new Error('Unknown content type');
            }

            await axiosPrivate.post(endpoint, {
                id: selectedItem.id,
                approve: reviewAction === 'approve',
                reviewComments: reviewComments || null
            });

            const successMessage = reviewAction === 'approve'
                ? t('contentApprovalApproveSuccess')
                : t('contentApprovalRejectSuccess');
            enqueueSnackbar(successMessage, { variant: 'success' });

            // Optimistically remove the item from state immediately
            removeItemFromState(selectedItem, type);

            closeReviewDialog();
            // Refresh counts in background
            fetchPendingCounts();
        } catch (error) {
            console.error('Error processing review action:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Quick approve (without dialog, for simple approvals)
    const handleQuickApprove = async (item, contentType) => {
        setProcessingAction(true);
        try {
            const type = contentType || item.contentType;
            let endpoint = '';
            switch (type) {
                case 'ToolPassport':
                    endpoint = '/ContentApproval/ApproveToolPassport';
                    break;
                case 'Criteria':
                    endpoint = '/ContentApproval/ApproveCriteria';
                    break;
                case 'CriteriaValue':
                    endpoint = '/ContentApproval/ApproveCriteriaValue';
                    break;
                case 'ToolFeature':
                    endpoint = '/ContentApproval/ApproveToolFeature';
                    break;
                case 'ToolPassportRequest':
                    endpoint = '/ContentApproval/ApproveToolPassportRequest';
                    break;
                case 'CriteriaRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaRequest';
                    break;
                case 'CriteriaValueRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaValueRequest';
                    break;
                case 'ToolFeatureRequest':
                    endpoint = '/ContentApproval/ApproveToolFeatureRequest';
                    break;
                default:
                    throw new Error('Unknown content type');
            }

            await axiosPrivate.post(endpoint, {
                id: item.id,
                approve: true
            });

            enqueueSnackbar(t('contentApprovalApproveSuccess'), { variant: 'success' });

            // Optimistically remove the item from state immediately
            removeItemFromState(item, type);

            // Refresh counts in background
            fetchPendingCounts();
        } catch (error) {
            console.error('Error approving item:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Batch approve criteria with all its pending values
    const handleApproveCriteriaWithValues = async (item) => {
        setProcessingAction(true);
        try {
            const response = await axiosPrivate.post('/ContentApproval/ApproveCriteriaWithValues', {
                id: item.id,
                approve: true,
                reviewComments: null
            });

            const valuesCount = response.data.approvedValuesCount || 0;
            enqueueSnackbar(
                t('contentApprovalCriteriaWithValuesSuccess', { count: valuesCount }) ||
                `Criteria and ${valuesCount} values approved successfully`,
                { variant: 'success' }
            );

            // Optimistically remove the criteria from state
            removeItemFromState(item, 'Criteria');

            // Refresh counts and data in background (to also update values if displayed)
            fetchPendingCounts();
            fetchData();
        } catch (error) {
            console.error('Error batch approving criteria with values:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Bulk approve all tool features for a specific tool
    const handleApproveToolFeaturesByTool = async (toolGroup, approve = true) => {
        setProcessingAction(true);
        try {
            await axiosPrivate.post('/ContentApproval/ApproveToolFeaturesByTool', {
                toolPassportId: toolGroup.toolPassportId,
                subcategoryId: toolGroup.subcategoryId,
                approve: approve,
                reviewComments: null
            });

            const successMessage = approve
                ? t('toolFeaturesApprovedForTool', { tool: toolGroup.toolPassportName, count: toolGroup.totalFeatures }) ||
                  `All ${toolGroup.totalFeatures} features for "${toolGroup.toolPassportName}" approved`
                : t('toolFeaturesRejectedForTool', { tool: toolGroup.toolPassportName, count: toolGroup.totalFeatures }) ||
                  `All ${toolGroup.totalFeatures} features for "${toolGroup.toolPassportName}" rejected`;

            enqueueSnackbar(successMessage, { variant: 'success' });

            // Remove the tool group from state
            setToolFeaturesGrouped(prev => prev.filter(
                g => !(g.toolPassportId === toolGroup.toolPassportId && g.subcategoryId === toolGroup.subcategoryId)
            ));

            // Also remove individual features from state
            const featureIds = toolGroup.features.map(f => f.id);
            setToolFeatures(prev => prev.filter(f => !featureIds.includes(f.id)));
            setAllPending(prev => prev.filter(i => !(featureIds.includes(i.id) && i.contentType === 'ToolFeature')));

            // Refresh counts in background
            fetchPendingCounts();
        } catch (error) {
            console.error('Error bulk approving tool features:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Handle accordion expansion
    const handleAccordionChange = (groupKey) => (event, isExpanded) => {
        setExpandedToolAccordions(prev => ({
            ...prev,
            [groupKey]: isExpanded
        }));
    };

    // Open reject dialog
    const openRejectDialog = (item, contentType) => {
        setSelectedItem({ ...item, contentType: contentType || item.contentType });
        setReviewComments("");
        setRejectDialogOpen(true);
    };

    // Close reject dialog
    const closeRejectDialog = () => {
        setRejectDialogOpen(false);
        setSelectedItem(null);
        setReviewComments("");
    };

    // Reject item
    const handleReject = async () => {
        if (!selectedItem) return;

        setProcessingAction(true);
        try {
            const type = selectedItem.contentType;
            let endpoint = '';
            switch (type) {
                case 'ToolPassport':
                    endpoint = '/ContentApproval/ApproveToolPassport';
                    break;
                case 'Criteria':
                    endpoint = '/ContentApproval/ApproveCriteria';
                    break;
                case 'CriteriaValue':
                    endpoint = '/ContentApproval/ApproveCriteriaValue';
                    break;
                case 'ToolFeature':
                    endpoint = '/ContentApproval/ApproveToolFeature';
                    break;
                case 'ToolPassportRequest':
                    endpoint = '/ContentApproval/ApproveToolPassportRequest';
                    break;
                case 'CriteriaRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaRequest';
                    break;
                case 'CriteriaValueRequest':
                    endpoint = '/ContentApproval/ApproveCriteriaValueRequest';
                    break;
                case 'ToolFeatureRequest':
                    endpoint = '/ContentApproval/ApproveToolFeatureRequest';
                    break;
                default:
                    throw new Error('Unknown content type');
            }

            await axiosPrivate.post(endpoint, {
                id: selectedItem.id,
                approve: false,
                reviewComments: reviewComments
            });

            enqueueSnackbar(t('contentApprovalRejectSuccess'), { variant: 'success' });

            // Optimistically remove the item from state immediately
            removeItemFromState(selectedItem, type);

            closeRejectDialog();
            // Refresh counts in background
            fetchPendingCounts();
        } catch (error) {
            console.error('Error rejecting item:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setProcessingAction(false);
        }
    };

    // Fetch and view changes for edit/delete requests
    const handleViewChanges = async (item, requestType) => {
        setLoadingChanges(true);
        setViewChangesDialogOpen(true);
        try {
            let endpoint = '';
            switch (requestType || item.contentType) {
                case 'ToolPassportRequest':
                    endpoint = `/ContentApproval/ToolPassportRequest/${item.id}`;
                    break;
                case 'CriteriaRequest':
                    endpoint = `/ContentApproval/CriteriaRequest/${item.id}`;
                    break;
                case 'CriteriaValueRequest':
                    endpoint = `/ContentApproval/CriteriaValueRequest/${item.id}`;
                    break;
                case 'ToolFeatureRequest':
                    endpoint = `/ContentApproval/ToolFeatureRequest/${item.id}`;
                    break;
                default:
                    throw new Error('Unknown request type');
            }
            const response = await axiosPrivate.get(endpoint);
            setViewChangesData({ ...response.data, requestContentType: requestType || item.contentType });
        } catch (error) {
            console.error('Error fetching request details:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
            setViewChangesDialogOpen(false);
        } finally {
            setLoadingChanges(false);
        }
    };

    // Close view changes dialog
    const closeViewChangesDialog = () => {
        setViewChangesDialogOpen(false);
        setViewChangesData(null);
    };

    // Content type chip renderer
    const renderContentTypeChip = (type) => {
        const typeConfig = {
            'ToolPassport': { color: 'primary', icon: <BuildIcon fontSize="small" /> },
            'Criteria': { color: 'secondary', icon: <ListAltIcon fontSize="small" /> },
            'CriteriaValue': { color: 'info', icon: <TuneIcon fontSize="small" /> },
            'ToolFeature': { color: 'warning', icon: <FeaturedPlayListIcon fontSize="small" /> },
            'ToolPassportRequest': { color: 'error', icon: <EditIcon fontSize="small" /> },
            'CriteriaRequest': { color: 'secondary', icon: <EditIcon fontSize="small" /> },
            'CriteriaValueRequest': { color: 'info', icon: <EditIcon fontSize="small" /> },
            'ToolFeatureRequest': { color: 'warning', icon: <EditIcon fontSize="small" /> }
        };
        const config = typeConfig[type] || { color: 'default' };
        return (
            <Chip
                size="small"
                color={config.color}
                label={t(`contentApprovalType${type}`) || type}
                icon={config.icon}
            />
        );
    };

    // Action buttons renderer
    const renderActions = (item, contentType) => {
        const type = contentType || item.contentType;
        const hasPendingValues = type === 'Criteria' && (item.pendingValuesCount > 0);
        const isRequestType = ['ToolPassportRequest', 'CriteriaRequest', 'CriteriaValueRequest', 'ToolFeatureRequest'].includes(type);

        return (
            <div className="d-flex justify-content-center align-items-center gap-1">
                {/* View Changes button for Request types */}
                {isRequestType && (
                    <Tooltip title={t('viewChanges') || "View Changes"}>
                        <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewChanges(item, type)}
                            disabled={processingAction}
                        >
                            <CompareArrowsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {/* Batch approve button for Criteria with pending values */}
                {hasPendingValues && (
                    <Tooltip title={t('approveCriteriaWithValues', { count: item.pendingValuesCount }) || `Approve with ${item.pendingValuesCount} values`}>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleApproveCriteriaWithValues(item)}
                            disabled={processingAction}
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            <PlaylistAddCheckIcon fontSize="small" />
                            <span style={{ fontSize: '10px', marginLeft: '2px' }}>{item.pendingValuesCount}</span>
                        </Button>
                    </Tooltip>
                )}
                <Tooltip title={t('quickApprove') || "Quick Approve"}>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleQuickApprove(item, contentType)}
                        disabled={processingAction}
                        sx={{ minWidth: 'auto', px: 1 }}
                    >
                        <CheckCircleIcon fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title={t('approveWithComment') || "Approve with Comment"}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => openReviewDialog(item, contentType, 'approve')}
                        disabled={processingAction}
                        sx={{ minWidth: 'auto', px: 1 }}
                    >
                        <CheckCircleIcon fontSize="small" />
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>+</span>
                    </Button>
                </Tooltip>
                <Tooltip title={t('rejectWithComment') || "Reject with Comment"}>
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => openReviewDialog(item, contentType, 'reject')}
                        disabled={processingAction}
                        sx={{ minWidth: 'auto', px: 1 }}
                    >
                        <CancelIcon fontSize="small" />
                    </Button>
                </Tooltip>
            </div>
        );
    };

    // Columns for "All Pending" tab
    const allPendingColumns = useMemo(() => [
        {
            field: 'contentType',
            headerName: t('contentApprovalContentType'),
            flex: 0.7,
            minWidth: 130,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderContentTypeChip(params.value),
        },
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 1.2,
            minWidth: 180,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'pendingValuesCount',
            headerName: t('pendingValues') || 'Values',
            flex: 0.4,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                // Only show for Criteria type
                if (params.row.contentType !== 'Criteria') return '-';
                return params.value > 0 ? (
                    <Chip
                        size="small"
                        color="info"
                        label={`${params.value}`}
                        icon={<TuneIcon style={{ fontSize: 14 }} />}
                    />
                ) : (
                    <span className="text-muted">0</span>
                );
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 1,
            minWidth: 220,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row),
        },
    ], [t, processingAction]);

    // Columns for Tool Passports tab
    const toolPassportColumns = useMemo(() => [
        {
            field: 'name',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'categoryName',
            headerName: t('category'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'typeOfToolName',
            headerName: t('typeOfTool'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'ToolPassport'),
        },
    ], [t, processingAction]);

    // Columns for combined Criteria & Values tab
    const criteriaAndValuesColumns = useMemo(() => [
        {
            field: 'itemType',
            headerName: t('type') || 'Type',
            flex: 0.5,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.value === 'Criteria') {
                    return <Chip size="small" color="secondary" icon={<ListAltIcon style={{ fontSize: 14 }} />} label={t('criteria')} />;
                }
                return <Chip size="small" color="info" variant="outlined" icon={<TuneIcon style={{ fontSize: 14 }} />} label={t('criteriaValue')} />;
            },
        },
        {
            field: 'displayName',
            headerName: t('name'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaName',
            headerName: t('parentCriteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.row.itemType === 'CriteriaValue' ? params.value : '-',
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'pendingValuesCount',
            headerName: t('pendingValues') || 'Pending Values',
            flex: 0.5,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.row.itemType !== 'Criteria') return '-';
                return params.value > 0 ? (
                    <Chip
                        size="small"
                        color="info"
                        label={`${params.value} / ${params.row.totalValuesCount || params.value}`}
                        icon={<TuneIcon style={{ fontSize: 14 }} />}
                    />
                ) : (
                    <span className="text-muted">-</span>
                );
            },
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.6,
            minWidth: 130,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 1,
            minWidth: 220,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, params.row.itemType),
        },
    ], [t, processingAction]);

    // Columns for Tool Features tab
    const toolFeatureColumns = useMemo(() => [
        {
            field: 'toolPassportName',
            headerName: t('toolPassport'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaName',
            headerName: t('criteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaValueText',
            headerName: t('criteriaValue'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'ToolFeature'),
        },
    ], [t, processingAction]);

    // Columns for Tool Passport Edit/Delete Requests tab
    const toolPassportRequestColumns = useMemo(() => [
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Chip
                    size="small"
                    color={params.value === 'Edit' ? 'warning' : 'error'}
                    icon={params.value === 'Edit' ? <EditIcon style={{ fontSize: 14 }} /> : <DeleteIcon style={{ fontSize: 14 }} />}
                    label={params.value === 'Edit' ? t('editRequest') : t('deleteRequest')}
                />
            ),
        },
        {
            field: 'originalToolName',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'name',
            headerName: t('proposedName'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'originalCategoryName',
            headerName: t('category'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'reason',
            headerName: t('reason'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'ToolPassportRequest'),
        },
    ], [t, processingAction]);

    // Columns for Criteria Edit/Delete Requests tab
    const criteriaRequestColumns = useMemo(() => [
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Chip
                    size="small"
                    color={params.value === 'Edit' ? 'warning' : 'error'}
                    icon={params.value === 'Edit' ? <EditIcon style={{ fontSize: 14 }} /> : <DeleteIcon style={{ fontSize: 14 }} />}
                    label={params.value === 'Edit' ? t('editRequest') : t('deleteRequest')}
                />
            ),
        },
        {
            field: 'originalCriteriaName',
            headerName: t('originalCriteria'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'name',
            headerName: t('proposedName'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'reason',
            headerName: t('reason'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'CriteriaRequest'),
        },
    ], [t, processingAction]);

    // Columns for CriteriaValue Edit/Delete Requests tab
    const criteriaValueRequestColumns = useMemo(() => [
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Chip
                    size="small"
                    color={params.value === 'Edit' ? 'warning' : 'error'}
                    icon={params.value === 'Edit' ? <EditIcon style={{ fontSize: 14 }} /> : <DeleteIcon style={{ fontSize: 14 }} />}
                    label={params.value === 'Edit' ? t('editRequest') : t('deleteRequest')}
                />
            ),
        },
        {
            field: 'originalValue',
            headerName: t('originalCriteriaValue'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'value',
            headerName: t('proposedValue'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'criteriaName',
            headerName: t('criteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'reason',
            headerName: t('reason'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'CriteriaValueRequest'),
        },
    ], [t, processingAction]);

    // Columns for ToolFeature Edit/Delete Requests tab
    const toolFeatureRequestColumns = useMemo(() => [
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Chip
                    size="small"
                    color={params.value === 'Edit' ? 'warning' : 'error'}
                    icon={params.value === 'Edit' ? <EditIcon style={{ fontSize: 14 }} /> : <DeleteIcon style={{ fontSize: 14 }} />}
                    label={params.value === 'Edit' ? t('editRequest') : t('deleteRequest')}
                />
            ),
        },
        {
            field: 'originalToolPassportName',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'originalCriteriaName',
            headerName: t('criteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'originalCriteriaValueName',
            headerName: t('criteriaValue'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'reason',
            headerName: t('reason'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, 'ToolFeatureRequest'),
        },
    ], [t, processingAction]);

    // Columns for "All Requests" combined view
    const allRequestsColumns = useMemo(() => [
        {
            field: 'contentType',
            headerName: t('contentType'),
            flex: 0.6,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderContentTypeChip(params.value),
        },
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Chip
                    size="small"
                    color={params.value === 'Edit' ? 'warning' : 'error'}
                    icon={params.value === 'Edit' ? <EditIcon style={{ fontSize: 14 }} /> : <DeleteIcon style={{ fontSize: 14 }} />}
                    label={params.value === 'Edit' ? t('editRequest') : t('deleteRequest')}
                />
            ),
        },
        {
            field: 'itemName',
            headerName: t('itemName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueGetter: (params, row) => {
                // Get item name based on content type
                switch (row.contentType) {
                    case 'ToolPassportRequest': return row.originalToolName || row.name || '-';
                    case 'CriteriaRequest': return row.originalCriteriaName || row.name || '-';
                    case 'CriteriaValueRequest': return row.originalValue || row.value || '-';
                    case 'ToolFeatureRequest': return row.originalToolPassportName || '-';
                    default: return '-';
                }
            },
        },
        {
            field: 'reason',
            headerName: t('reason'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderActions(params.row, params.row.contentType),
        },
    ], [t, processingAction]);

    // Get columns based on main category and sub type
    const getColumns = () => {
        if (mainCategory === 'submissions') {
            switch (subType) {
                case 'all': return allPendingColumns;
                case 'toolPassport': return toolPassportColumns;
                case 'criteria': return criteriaAndValuesColumns;
                case 'toolFeature': return toolFeatureColumns;
                default: return allPendingColumns;
            }
        } else {
            // requests
            switch (subType) {
                case 'all': return allRequestsColumns;
                case 'toolPassport': return toolPassportRequestColumns;
                case 'criteria': return criteriaRequestColumns;
                case 'criteriaValue': return criteriaValueRequestColumns;
                case 'toolFeature': return toolFeatureRequestColumns;
                default: return allRequestsColumns;
            }
        }
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{t("contentApprovalListTitle")}</h4>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-lg-3 col-md-6 col-sm-6 mb-2">
                    <Card
                        sx={{
                            bgcolor: mainCategory === 'submissions' ? 'primary.main' : 'background.paper',
                            color: mainCategory === 'submissions' ? 'primary.contrastText' : 'text.primary',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                        }}
                        onClick={() => { setMainCategory('submissions'); setSubType('all'); }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div">
                                {(pendingCounts.pendingToolPassports || 0) +
                                 (pendingCounts.pendingCriteria || 0) +
                                 (pendingCounts.pendingCriteriaValues || 0) +
                                 (pendingCounts.pendingToolFeatures || 0)}
                            </Typography>
                            <Typography variant="body2">
                                {t('newSubmissions')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {pendingCounts.pendingToolPassports} {t('tools')}, {pendingCounts.pendingCriteria || 0}+{pendingCounts.pendingCriteriaValues || 0} {t('criteriaAndValues')}, {pendingCounts.pendingToolFeatures} {t('features')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-6 mb-2">
                    <Card
                        sx={{
                            bgcolor: mainCategory === 'requests' ? 'error.main' : 'background.paper',
                            color: mainCategory === 'requests' ? 'error.contrastText' : 'text.primary',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                        }}
                        onClick={() => { setMainCategory('requests'); setSubType('all'); }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div">
                                {(pendingCounts.pendingToolPassportRequests || 0) +
                                 (pendingCounts.pendingCriteriaRequests || 0) +
                                 (pendingCounts.pendingCriteriaValueRequests || 0) +
                                 (pendingCounts.pendingToolFeatureRequests || 0)}
                            </Typography>
                            <Typography variant="body2">
                                {t('editDeleteRequests')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {pendingCounts.pendingToolPassportRequests} {t('tools')}, {pendingCounts.pendingCriteriaRequests || 0}+{pendingCounts.pendingCriteriaValueRequests || 0} {t('criteriaAndValues')}, {pendingCounts.pendingToolFeatureRequests} {t('features')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-6 mb-2">
                    <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div">
                                {pendingCounts.totalPending || 0}
                            </Typography>
                            <Typography variant="body2">
                                {t('contentApprovalAllPending')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Two-tier Navigation */}
            <Box sx={{ mb: 3 }}>
                {/* Main Category Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        value={mainCategory}
                        exclusive
                        onChange={handleMainCategoryChange}
                        aria-label="main category"
                        size="small"
                    >
                        <ToggleButton value="submissions" sx={{ px: 3 }}>
                            <Badge badgeContent={(pendingCounts.pendingToolPassports || 0) + (pendingCounts.pendingCriteria || 0) + (pendingCounts.pendingCriteriaValues || 0) + (pendingCounts.pendingToolFeatures || 0)} color="primary" max={99}>
                                <span style={{ paddingRight: 8 }}>{t('newSubmissions')}</span>
                            </Badge>
                        </ToggleButton>
                        <ToggleButton value="requests" sx={{ px: 3 }}>
                            <Badge badgeContent={(pendingCounts.pendingToolPassportRequests || 0) + (pendingCounts.pendingCriteriaRequests || 0) + (pendingCounts.pendingCriteriaValueRequests || 0) + (pendingCounts.pendingToolFeatureRequests || 0)} color="error" max={99}>
                                <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                                <span style={{ paddingRight: 8 }}>{t('editDeleteRequests')}</span>
                            </Badge>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Sub-type Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            value={subType}
                            onChange={handleSubTypeChange}
                            displayEmpty
                        >
                            {mainCategory === 'submissions' ? (
                                [
                                    <MenuItem key="all" value="all">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {t('all')}
                                            <Chip size="small" label={pendingCounts.totalPending - ((pendingCounts.pendingToolPassportRequests || 0) + (pendingCounts.pendingCriteriaRequests || 0) + (pendingCounts.pendingCriteriaValueRequests || 0) + (pendingCounts.pendingToolFeatureRequests || 0))} color="default" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="toolPassport" value="toolPassport">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BuildIcon fontSize="small" />
                                            {t('contentApprovalTypeToolPassport')}
                                            <Chip size="small" label={pendingCounts.pendingToolPassports || 0} color="primary" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="criteria" value="criteria">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ListAltIcon fontSize="small" />
                                            {t('criteriaAndValues')}
                                            <Chip size="small" label={`${pendingCounts.pendingCriteria || 0}+${pendingCounts.pendingCriteriaValues || 0}`} color="secondary" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="toolFeature" value="toolFeature">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FeaturedPlayListIcon fontSize="small" />
                                            {t('contentApprovalTypeToolFeature')}
                                            <Chip size="small" label={pendingCounts.pendingToolFeatures || 0} color="warning" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>
                                ]
                            ) : (
                                [
                                    <MenuItem key="all" value="all">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {t('all')}
                                            <Chip size="small" label={(pendingCounts.pendingToolPassportRequests || 0) + (pendingCounts.pendingCriteriaRequests || 0) + (pendingCounts.pendingCriteriaValueRequests || 0) + (pendingCounts.pendingToolFeatureRequests || 0)} color="default" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="toolPassport" value="toolPassport">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BuildIcon fontSize="small" />
                                            {t('contentApprovalTypeToolPassportRequest')}
                                            <Chip size="small" label={pendingCounts.pendingToolPassportRequests || 0} color="error" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="criteria" value="criteria">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ListAltIcon fontSize="small" />
                                            {t('contentApprovalTypeCriteriaRequest')}
                                            <Chip size="small" label={pendingCounts.pendingCriteriaRequests || 0} color="secondary" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="criteriaValue" value="criteriaValue">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TuneIcon fontSize="small" />
                                            {t('contentApprovalTypeCriteriaValueRequest')}
                                            <Chip size="small" label={pendingCounts.pendingCriteriaValueRequests || 0} color="info" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>,
                                    <MenuItem key="toolFeature" value="toolFeature">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FeaturedPlayListIcon fontSize="small" />
                                            {t('contentApprovalTypeToolFeatureRequest')}
                                            <Chip size="small" label={pendingCounts.pendingToolFeatureRequests || 0} color="warning" sx={{ height: 20 }} />
                                        </Box>
                                    </MenuItem>
                                ]
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Search */}
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ maxWidth: '300px' }}
                />
            </div>

            {/* View Mode Toggle for Tool Features */}
            {mainCategory === 'submissions' && subType === 'toolFeature' && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={toolFeaturesViewMode === 'grouped'}
                                onChange={(e) => setToolFeaturesViewMode(e.target.checked ? 'grouped' : 'individual')}
                                color="primary"
                            />
                        }
                        label={t('groupByTool') || 'Group by Tool'}
                    />
                    {toolFeaturesViewMode === 'grouped' && toolFeaturesGrouped.length > 0 && (
                        <Typography variant="body2" color="textSecondary">
                            {t('toolsWithPendingFeatures', { count: toolFeaturesGrouped.length }) || `${toolFeaturesGrouped.length} tools with pending features`}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Data Table or Grouped View */}
            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : mainCategory === 'submissions' && subType === 'toolFeature' && toolFeaturesViewMode === 'grouped' ? (
                // Grouped Tool Features View
                toolFeaturesGrouped.length === 0 ? (
                    <div className="text-center p-5">
                        <Typography variant="h6" color="textSecondary">
                            {t('contentApprovalNoPending')}
                        </Typography>
                    </div>
                ) : (
                    <Box>
                        {toolFeaturesGrouped.map((toolGroup) => {
                            const groupKey = `${toolGroup.toolPassportId}-${toolGroup.subcategoryId}`;
                            const isExpanded = expandedToolAccordions[groupKey] || false;

                            // Group features by weight
                            const featuresByWeight = {
                                w1: toolGroup.features?.filter(f => f.weightCoefficient === 1.00) || [],
                                w05: toolGroup.features?.filter(f => f.weightCoefficient === 0.50) || [],
                                w033: toolGroup.features?.filter(f => f.weightCoefficient === 0.33) || []
                            };

                            return (
                                <Accordion
                                    key={groupKey}
                                    expanded={isExpanded}
                                    onChange={handleAccordionChange(groupKey)}
                                    sx={{ mb: 1 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            bgcolor: 'background.default',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                            <BuildIcon color="primary" />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {toolGroup.toolPassportName}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {toolGroup.subcategoryName}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label={`${toolGroup.totalFeatures} ${t('features') || 'features'}`}
                                            />
                                            {/* Bulk Action Buttons */}
                                            <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title={t('approveAllForTool') || 'Approve All'}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => handleApproveToolFeaturesByTool(toolGroup, true)}
                                                        disabled={processingAction}
                                                        startIcon={<PlaylistAddCheckIcon />}
                                                    >
                                                        {t('approveAll') || 'Approve All'}
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title={t('rejectAllForTool') || 'Reject All'}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => handleApproveToolFeaturesByTool(toolGroup, false)}
                                                        disabled={processingAction}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                                        {/* W=1.00 Criteria (Red) */}
                                        {featuresByWeight.w1.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: '#d32f2f',
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 12, height: 12, borderRadius: '50%',
                                                        bgcolor: '#d32f2f'
                                                    }} />
                                                    W = 1.00 ({featuresByWeight.w1.length})
                                                </Typography>
                                                {featuresByWeight.w1.map(feature => (
                                                    <Box
                                                        key={feature.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 1,
                                                            bgcolor: 'white',
                                                            borderRadius: 1,
                                                            mb: 0.5,
                                                            borderLeft: '3px solid #d32f2f'
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {feature.criteriaName}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {feature.criteriaValueText}
                                                            </Typography>
                                                        </Box>
                                                        {renderActions(feature, 'ToolFeature')}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {/* W=0.50 Criteria (Orange) */}
                                        {featuresByWeight.w05.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: '#ed6c02',
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 12, height: 12, borderRadius: '50%',
                                                        bgcolor: '#ed6c02'
                                                    }} />
                                                    W = 0.50 ({featuresByWeight.w05.length})
                                                </Typography>
                                                {featuresByWeight.w05.map(feature => (
                                                    <Box
                                                        key={feature.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 1,
                                                            bgcolor: 'white',
                                                            borderRadius: 1,
                                                            mb: 0.5,
                                                            borderLeft: '3px solid #ed6c02'
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {feature.criteriaName}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {feature.criteriaValueText}
                                                            </Typography>
                                                        </Box>
                                                        {renderActions(feature, 'ToolFeature')}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {/* W=0.33 Criteria (Green) */}
                                        {featuresByWeight.w033.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: '#2e7d32',
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 12, height: 12, borderRadius: '50%',
                                                        bgcolor: '#2e7d32'
                                                    }} />
                                                    W = 0.33 ({featuresByWeight.w033.length})
                                                </Typography>
                                                {featuresByWeight.w033.map(feature => (
                                                    <Box
                                                        key={feature.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 1,
                                                            bgcolor: 'white',
                                                            borderRadius: 1,
                                                            mb: 0.5,
                                                            borderLeft: '3px solid #2e7d32'
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {feature.criteriaName}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {feature.criteriaValueText}
                                                            </Typography>
                                                        </Box>
                                                        {renderActions(feature, 'ToolFeature')}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                )
            ) : filteredData.length === 0 ? (
                <div className="text-center p-5">
                    <Typography variant="h6" color="textSecondary">
                        {t('contentApprovalNoPending')}
                    </Typography>
                </div>
            ) : (
                <DataTable
                    key={`${mainCategory}-${subType}-${dataVersion}`}
                    rows={filteredData}
                    columns={getColumns()}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    localeTextsMap={localizedTextsMap}
                    getRowId={(mainCategory === 'submissions' && subType === 'criteria') || (mainCategory === 'requests' && subType === 'all') ? (row) => row.uniqueId : undefined}
                />
            )}

            {/* Review Dialog (for both approve and reject with comments) */}
            <Dialog open={reviewDialogOpen} onClose={closeReviewDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {reviewAction === 'approve' ? t('contentApprovalApproveTitle') : t('contentApprovalRejectTitle')}
                </DialogTitle>
                <DialogContent>
                    <p className="mb-3">
                        {reviewAction === 'approve'
                            ? t('contentApprovalApproveConfirm', { name: selectedItem?.name || selectedItem?.value || '' })
                            : t('contentApprovalRejectConfirm', { name: selectedItem?.name || selectedItem?.value || '' })
                        }
                    </p>
                    <TextField
                        label={t('contentApprovalReviewComments')}
                        multiline
                        rows={3}
                        fullWidth
                        value={reviewComments}
                        onChange={(e) => setReviewComments(e.target.value)}
                        placeholder={reviewAction === 'approve'
                            ? t('contentApprovalApproveCommentsPlaceholder') || "Optional: Add approval notes..."
                            : t('contentApprovalReviewCommentsPlaceholder')
                        }
                        required={reviewAction === 'reject'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReviewDialog} disabled={processingAction}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleReviewAction}
                        color={reviewAction === 'approve' ? "success" : "error"}
                        variant="contained"
                        disabled={processingAction || (reviewAction === 'reject' && !reviewComments.trim())}
                    >
                        {processingAction ? <CircularProgress size={24} /> : (reviewAction === 'approve' ? t('approve') : t('reject'))}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Legacy Rejection Dialog (keeping for backward compatibility) */}
            <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('contentApprovalRejectTitle')}</DialogTitle>
                <DialogContent>
                    <p className="mb-3">
                        {t('contentApprovalRejectConfirm', {
                            name: selectedItem?.name || selectedItem?.value || ''
                        })}
                    </p>
                    <TextField
                        label={t('contentApprovalReviewComments')}
                        multiline
                        rows={3}
                        fullWidth
                        value={reviewComments}
                        onChange={(e) => setReviewComments(e.target.value)}
                        placeholder={t('contentApprovalReviewCommentsPlaceholder')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRejectDialog} disabled={processingAction}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleReject}
                        color="error"
                        variant="contained"
                        disabled={processingAction}
                    >
                        {processingAction ? <CircularProgress size={24} /> : t('reject')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Changes Dialog for Edit/Delete Requests */}
            <Dialog
                open={viewChangesDialogOpen}
                onClose={closeViewChangesDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { minHeight: '60vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white' }}>
                    <CompareArrowsIcon />
                    {t('viewChangesTitle') || 'View Changes'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {loadingChanges ? (
                        <div className="d-flex justify-content-center p-5">
                            <CircularProgress />
                        </div>
                    ) : viewChangesData ? (
                        <>
                            {/* Request Info */}
                            <Alert
                                severity={viewChangesData.requestType === 'Edit' ? 'warning' : 'error'}
                                icon={viewChangesData.requestType === 'Edit' ? <EditIcon /> : <DeleteIcon />}
                                sx={{ mb: 3 }}
                            >
                                <strong>{viewChangesData.requestType === 'Edit' ? t('editRequest') : t('deleteRequest')}</strong>
                                {viewChangesData.reason && (
                                    <span> - {t('reason')}: {viewChangesData.reason}</span>
                                )}
                            </Alert>

                            {/* Render content based on request content type */}
                            {viewChangesData.requestContentType === 'ToolPassportRequest' && (
                                viewChangesData.requestType === 'Delete' ? (
                                    <Box>
                                        <Typography variant="h6" gutterBottom color="error">
                                            {t('toolToBeDeleted') || 'Tool to be Deleted'}
                                        </Typography>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {renderComparisonRow(t('toolPassportName'), viewChangesData.original?.name)}
                                                {renderComparisonRow(t('panel'), viewChangesData.original?.panelName)}
                                                {renderComparisonRow(t('category'), viewChangesData.original?.categoryName)}
                                                {renderComparisonRow(t('subcategory'), viewChangesData.original?.subcategoryName)}
                                                {renderComparisonRow(t('typeOfTool'), viewChangesData.original?.typeOfToolName)}
                                                {renderComparisonRow(t('toolPassportToolURL'), viewChangesData.original?.toolURL)}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Box>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                                    {t('originalValues') || 'Original Values'}
                                                </Typography>
                                            </div>
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                                                    {t('proposedChanges') || 'Proposed Changes'}
                                                </Typography>
                                            </div>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5' }}>
                                                    <th style={comparisonHeaderStyle}>{t('field') || 'Field'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('originalValues') || 'Original'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('proposedChanges') || 'Proposed'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderComparisonTableRow(t('toolPassportName'), viewChangesData.original?.name, viewChangesData.proposed?.name, false, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportToolURL'), viewChangesData.original?.toolURL, viewChangesData.proposed?.toolURL, false, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportFunctions'), viewChangesData.original?.function, viewChangesData.proposed?.function, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportAbstractionLevel'), viewChangesData.original?.abstractionLevel, viewChangesData.proposed?.abstractionLevel, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportIOConnection'), viewChangesData.original?.ioConnection, viewChangesData.proposed?.ioConnection, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportVerificationTool'), viewChangesData.original?.verificationTool, viewChangesData.proposed?.verificationTool, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportApplicationAreaOrEquivalenceCoverage'), viewChangesData.original?.applicationAreaOrEquivalenceCoverage, viewChangesData.proposed?.applicationAreaOrEquivalenceCoverage, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportQualitativeFeature'), viewChangesData.original?.qualitativeFeature, viewChangesData.proposed?.qualitativeFeature, true, t('changed'))}
                                                {renderComparisonTableRow(t('toolPassportQuantitativeFeature'), viewChangesData.original?.quantitativeFeature, viewChangesData.proposed?.quantitativeFeature, true, t('changed'))}
                                                {(viewChangesData.original?.logo || viewChangesData.proposed?.logo) && (
                                                    <tr>
                                                        <td style={comparisonCellStyle}><strong>{t('logo')}</strong></td>
                                                        <td style={comparisonCellStyle}>
                                                            {viewChangesData.original?.logo && (
                                                                <img
                                                                    src={`${process.env.REACT_APP_SERVER_URL}/${viewChangesData.original.logo.replace(/^\/+/, '')}`}
                                                                    alt="Original Logo"
                                                                    style={{ maxHeight: '60px', maxWidth: '100px' }}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            )}
                                                        </td>
                                                        <td style={{
                                                            ...comparisonCellStyle,
                                                            background: viewChangesData.proposed?.logo && viewChangesData.proposed?.logo !== viewChangesData.original?.logo ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {viewChangesData.proposed?.logo && (
                                                                <img
                                                                    src={`${process.env.REACT_APP_SERVER_URL}/${viewChangesData.proposed.logo.replace(/^\/+/, '')}`}
                                                                    alt="Proposed Logo"
                                                                    style={{ maxHeight: '60px', maxWidth: '100px' }}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </Box>
                                )
                            )}

                            {/* Criteria Request View */}
                            {viewChangesData.requestContentType === 'CriteriaRequest' && (
                                viewChangesData.requestType === 'Delete' ? (
                                    <Box>
                                        <Typography variant="h6" gutterBottom color="error">
                                            {t('criteriaToBeDeleted') || 'Criteria to be Deleted'}
                                        </Typography>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {renderComparisonRow(t('criteriaName'), viewChangesData.original?.name)}
                                                {renderComparisonRow(t('panel'), viewChangesData.original?.panelName)}
                                                {renderComparisonRow(t('category'), viewChangesData.original?.categoryName)}
                                                {renderComparisonRow(t('subcategory'), viewChangesData.original?.subcategoryName)}
                                                {renderComparisonRow(t('weightCoefficient'), viewChangesData.original?.weightCoefficient?.toFixed(2))}
                                                {renderComparisonRow(t('isMandatory'), viewChangesData.original?.isMandatory ? t('yes') : t('no'))}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Box>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                                    {t('originalValues') || 'Original Values'}
                                                </Typography>
                                            </div>
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                                                    {t('proposedChanges') || 'Proposed Changes'}
                                                </Typography>
                                            </div>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5' }}>
                                                    <th style={comparisonHeaderStyle}>{t('field') || 'Field'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('originalValues') || 'Original'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('proposedChanges') || 'Proposed'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderComparisonTableRow(t('criteriaName'), viewChangesData.original?.name, viewChangesData.proposed?.name, false, t('changed'))}
                                                {renderComparisonTableRow(t('weightCoefficient'), viewChangesData.original?.weightCoefficient?.toFixed(2), viewChangesData.proposed?.weightCoefficient?.toFixed(2), false, t('changed'))}
                                                {renderComparisonTableRow(t('isMandatory'), viewChangesData.original?.isMandatory ? t('yes') : t('no'), viewChangesData.proposed?.isMandatory ? t('yes') : t('no'), false, t('changed'))}
                                            </tbody>
                                        </table>
                                    </Box>
                                )
                            )}

                            {/* CriteriaValue Request View */}
                            {viewChangesData.requestContentType === 'CriteriaValueRequest' && (
                                viewChangesData.requestType === 'Delete' ? (
                                    <Box>
                                        <Typography variant="h6" gutterBottom color="error">
                                            {t('criteriaValueToBeDeleted') || 'Criteria Value to be Deleted'}
                                        </Typography>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {renderComparisonRow(t('criteriaValue'), viewChangesData.original?.value)}
                                                {renderComparisonRow(t('criteria'), viewChangesData.original?.criteriaName)}
                                                {renderComparisonRow(t('subcategory'), viewChangesData.original?.subcategoryName)}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Box>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                                    {t('originalValues') || 'Original Values'}
                                                </Typography>
                                            </div>
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                                                    {t('proposedChanges') || 'Proposed Changes'}
                                                </Typography>
                                            </div>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5' }}>
                                                    <th style={comparisonHeaderStyle}>{t('field') || 'Field'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('originalValues') || 'Original'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('proposedChanges') || 'Proposed'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderComparisonTableRow(t('criteriaValue'), viewChangesData.original?.value, viewChangesData.proposed?.value, false, t('changed'))}
                                                {renderComparisonTableRow(t('criteria'), viewChangesData.original?.criteriaName, null, false, t('changed'))}
                                            </tbody>
                                        </table>
                                    </Box>
                                )
                            )}

                            {/* ToolFeature Request View */}
                            {viewChangesData.requestContentType === 'ToolFeatureRequest' && (
                                viewChangesData.requestType === 'Delete' ? (
                                    <Box>
                                        <Typography variant="h6" gutterBottom color="error">
                                            {t('toolFeatureToBeDeleted') || 'Tool Feature to be Deleted'}
                                        </Typography>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {renderComparisonRow(t('toolPassportName'), viewChangesData.original?.toolPassportName)}
                                                {renderComparisonRow(t('criteria'), viewChangesData.original?.criteriaName)}
                                                {renderComparisonRow(t('criteriaValue'), viewChangesData.original?.criteriaValueName)}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Box>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                                    {t('originalValues') || 'Original Values'}
                                                </Typography>
                                            </div>
                                            <div className="col-6">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                                                    {t('proposedChanges') || 'Proposed Changes'}
                                                </Typography>
                                            </div>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5' }}>
                                                    <th style={comparisonHeaderStyle}>{t('field') || 'Field'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('originalValues') || 'Original'}</th>
                                                    <th style={comparisonHeaderStyle}>{t('proposedChanges') || 'Proposed'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderComparisonTableRow(t('toolPassportName'), viewChangesData.original?.toolPassportName, null, false, t('changed'))}
                                                {renderComparisonTableRow(t('criteria'), viewChangesData.original?.criteriaName, null, false, t('changed'))}
                                                {renderComparisonTableRow(t('criteriaValue'), viewChangesData.original?.criteriaValueName, null, false, t('changed'))}
                                            </tbody>
                                        </table>
                                    </Box>
                                )
                            )}

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary">
                                {t('submittedBy')}: {viewChangesData.createdBy} | {t('createdDate')}: {viewChangesData.createdDate ? format(new Date(viewChangesData.createdDate), "dd/MM/yyyy HH:mm") : '-'}
                            </Typography>
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeViewChangesDialog} variant="outlined">
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Styles for comparison table
const comparisonHeaderStyle = {
    padding: '10px 12px',
    borderBottom: '2px solid #e0e0e0',
    textAlign: 'left',
    fontWeight: 600
};

const comparisonCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
    maxWidth: '300px',
    wordBreak: 'break-word'
};

// Helper to render a simple row (for delete requests)
const renderComparisonRow = (label, value) => {
    if (!value) return null;
    return (
        <tr>
            <td style={{ ...comparisonCellStyle, width: '30%', fontWeight: 600 }}>{label}</td>
            <td style={comparisonCellStyle}>{value}</td>
        </tr>
    );
};

// Helper to render comparison table row with highlighting
const renderComparisonTableRow = (label, originalValue, proposedValue, isHtml = false, changedLabel = 'Changed') => {
    const hasChange = proposedValue && proposedValue !== originalValue;

    const renderValue = (value) => {
        if (!value) return <span style={{ color: '#9e9e9e' }}>-</span>;
        if (isHtml) {
            // Strip HTML tags for display, but show first 200 chars
            const strippedText = value.replace(/<[^>]*>/g, '').substring(0, 200);
            return strippedText + (value.length > 200 ? '...' : '');
        }
        return value;
    };

    return (
        <tr>
            <td style={{ ...comparisonCellStyle, width: '25%', fontWeight: 600 }}>{label}</td>
            <td style={comparisonCellStyle}>{renderValue(originalValue)}</td>
            <td style={{
                ...comparisonCellStyle,
                background: hasChange ? '#fff3e0' : 'inherit',
                color: hasChange ? '#e65100' : 'inherit',
                fontWeight: hasChange ? 500 : 'normal'
            }}>
                {renderValue(proposedValue)}
                {hasChange && <Chip size="small" label={changedLabel} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
            </td>
        </tr>
    );
};

export default observer(List);
